import { useEffect, useState } from 'react';
import styles from './FormModal.module.css';
import Button from '../Button/Button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import schema, { FormSchema } from '../../../shared/formSchema';
import { getAuthContext } from '../../context/AuthContext';
import { Plus, SquarePen } from 'lucide-react';
import type { Project } from '../../../shared/types';

interface FormModalProps {
  data?: Project;
  mode?: 'add' | 'edit';
  onSucessfullSubmit: () => void;
}

const FormModal = ({ data, mode = 'add', onSucessfullSubmit }: FormModalProps) => {
  const [showModal, setShowModal] = useState(false);

  const { session, user, isLoading } = getAuthContext();

  const defaultValues: FormSchema =
    mode === 'edit' && data
      ? {
          projectName: data.project_name,
          projectUrl: data.link,
          projectImage: data.image,
          projectDescription: data.project_description,
          projectTechnologies: data.technologies,
        }
      : {
          projectName: '',
          projectUrl: '',
          projectImage: '',
          projectDescription: '',
          projectTechnologies: [],
        };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setError,
    reset,
  } = useForm<FormSchema>({
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      onSucessfullSubmit();
      setShowModal(false);
    }
  }, [isSubmitSuccessful, onSucessfullSubmit]);

  useEffect(() => {
    if (showModal) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, reset]);

  const onSubmit = async (formDataToSubmit: FormSchema) => {
    if (mode === 'edit' && data) {
      await updateCard({ ...formDataToSubmit, id: data.id });
    } else {
      try {
        const url = `${
          import.meta.env.VITE_BASE_URL || 'https://mellem.dev'
        }/api/v1/add/project/`;

        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${session?.token_type} ${session?.access_token}`,
          },
          body: JSON.stringify(formDataToSubmit),
        });
      } catch (error) {
        setError('root', {
          message: error instanceof Error ? error.message : 'An error occurred',
        });
      }
    }
  };

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const updateCard = async (formDataToUpdate: FormSchema & { id: number }) => {
    try {
      const url = `${
        import.meta.env.VITE_BASE_URL || 'https://mellem.dev'
      }/api/v1/update/project/`;
      const options: RequestInit = {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${session?.token_type} ${session?.access_token}`,
        },
        body: JSON.stringify(formDataToUpdate),
      };
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error('Failed to update project');
      }

      const result = await response.json();

      if (result.success) {
        onSucessfullSubmit();
      }
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  return (
    <>
      {user && !isLoading && (
        <div className={styles.addProject}>
          <Button
            className={mode === 'edit' ? 'editButton' : 'addButton'}
            onClick={toggleModal}
          >
            {mode === 'edit' ? (
              <SquarePen className={styles.addIcon} />
            ) : (
              <Plus className={styles.addIcon} />
            )}
            {mode === 'edit' ? 'Edit' : ''}
          </Button>
        </div>
      )}
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>
              {mode === 'edit' ? 'Edit Project' : 'Add New Project'}
            </h2>

            <form className={styles.modalForm} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.formGroup}>
                <label htmlFor="projectName">Project Name:</label>
                <input {...register('projectName')} type="text" id="projectName" />
                {errors.projectName && (
                  <span className={styles.error}>{errors.projectName.message}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="projectUrl">Project URL:</label>
                <input {...register('projectUrl')} type="text" id="projectUrl" />
                {errors.projectUrl && (
                  <span className={styles.error}>{errors.projectUrl.message}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="projectImage">Project Image URL:</label>
                <input {...register('projectImage')} type="text" id="projectImage" />
                {errors.projectImage && (
                  <span className={styles.error}>{errors.projectImage.message}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="projectDescription">Description:</label>
                <textarea
                  {...register('projectDescription')}
                  id="projectDescription"
                  className={styles.textarea}
                />
                {errors.projectDescription && (
                  <span className={styles.error}>{errors.projectDescription.message}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="projectTechnologies">Technologies Used:</label>
                <select
                  {...register('projectTechnologies')}
                  id="projectTechnologies"
                  multiple
                  required
                >
                  <option value="React">React</option>
                  <option value="Node.js">Node.js</option>
                  <option value="CSS">CSS</option>
                  <option value="JavaScript">JavaScript</option>
                </select>
              </div>
              <Button type="submit" className="submitButton">
                {mode === 'edit'
                  ? isSubmitting
                    ? 'Updating...'
                    : 'Update Project'
                  : isSubmitting
                  ? 'Submitting...'
                  : 'Add Project'}
              </Button>

              {errors.root && <span className={styles.success}>{errors.root.message}</span>}
            </form>
            <Button className="closeButton" onClick={toggleModal}>
              X
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default FormModal;

